import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiZap, FiPlus, FiX, FiCheck, FiClock, FiMail, FiFileText, FiUser,
  FiAlertCircle, FiSettings, FiPlay, FiPause, FiTrash2, FiEdit3,
  FiCalendar, FiMessageSquare, FiUpload, FiDownload, FiShield, FiToggleLeft,
  FiToggleRight, FiRefreshCw, FiActivity, FiTrendingUp, FiChevronRight,
  FiFilter, FiSearch, FiCpu, FiTarget, FiGift, FiDollarSign, FiBriefcase,
  FiUserPlus, FiBell, FiSend, FiDatabase, FiLayers, FiCode, FiBookOpen
} = FiIcons;

const mockAutomations = [
  {
    id: 1,
    name: 'Welcome Email Sequence',
    description: 'Send automated welcome emails to new users with onboarding resources',
    category: 'Communication',
    trigger: 'User Registration',
    actions: ['Send Email', 'Assign Onboarding Tasks', 'Create Welcome Message'],
    status: 'active',
    executions: 342,
    lastRun: '2024-01-15 14:30:00',
    created: '2024-01-10',
    scope: 'Platform-wide',
    aiPowered: true
  },
  {
    id: 2,
    name: 'Referral Reward Distribution',
    description: 'Automatically award points when referral milestones are reached',
    category: 'Rewards',
    trigger: 'Referral Completed',
    actions: ['Award Points', 'Send Notification', 'Update Leaderboard'],
    status: 'active',
    executions: 156,
    lastRun: '2024-01-15 10:20:00',
    created: '2024-01-08',
    scope: 'Referral System',
    aiPowered: false
  },
  {
    id: 3,
    name: 'Inactive User Re-engagement',
    description: 'Send personalized re-engagement campaigns to inactive users',
    category: 'Marketing',
    trigger: 'Schedule (Weekly)',
    actions: ['Analyze User Behavior', 'Generate Personalized Content', 'Send Campaign'],
    status: 'active',
    executions: 89,
    lastRun: '2024-01-14 09:00:00',
    created: '2024-01-05',
    scope: 'Platform-wide',
    aiPowered: true
  },
  {
    id: 4,
    name: 'Document Expiry Alerts',
    description: 'Alert users when important documents are about to expire',
    category: 'Dealrooms',
    trigger: 'Schedule (Daily)',
    actions: ['Check Document Dates', 'Send Alert Email', 'Create Task'],
    status: 'active',
    executions: 234,
    lastRun: '2024-01-15 08:00:00',
    created: '2024-01-01',
    scope: 'Dealrooms',
    aiPowered: false
  },
  {
    id: 5,
    name: 'Smart Content Recommendations',
    description: 'AI-powered content suggestions based on user behavior and preferences',
    category: 'AI',
    trigger: 'User Login',
    actions: ['Analyze Behavior', 'Generate Recommendations', 'Display Suggestions'],
    status: 'beta',
    executions: 1247,
    lastRun: '2024-01-15 15:45:00',
    created: '2024-01-12',
    scope: 'Platform-wide',
    aiPowered: true
  },
  {
    id: 6,
    name: 'Payment Reminder System',
    description: 'Automated reminders for upcoming and overdue payments',
    category: 'Billing',
    trigger: 'Schedule (Daily)',
    actions: ['Check Payment Status', 'Send Reminder', 'Log Activity'],
    status: 'active',
    executions: 67,
    lastRun: '2024-01-15 07:00:00',
    created: '2024-01-03',
    scope: 'Billing System',
    aiPowered: false
  },
  {
    id: 7,
    name: 'Team Performance Analytics',
    description: 'Generate weekly team performance reports and insights',
    category: 'Analytics',
    trigger: 'Schedule (Weekly)',
    actions: ['Collect Data', 'Analyze Trends', 'Generate Report', 'Distribute to Managers'],
    status: 'paused',
    executions: 12,
    lastRun: '2024-01-08 06:00:00',
    created: '2023-12-20',
    scope: 'Team Management',
    aiPowered: true
  },
  {
    id: 8,
    name: 'Challenge Completion Rewards',
    description: 'Automatically award rewards when users complete challenges',
    category: 'Rewards',
    trigger: 'Challenge Completed',
    actions: ['Verify Completion', 'Award Points', 'Send Congratulations', 'Update Profile'],
    status: 'active',
    executions: 423,
    lastRun: '2024-01-15 16:20:00',
    created: '2024-01-07',
    scope: 'Challenges',
    aiPowered: false
  }
];

const triggerTypes = [
  { id: 'user_registration', label: 'User Registration', icon: FiUser, category: 'User Events' },
  { id: 'user_login', label: 'User Login', icon: FiUser, category: 'User Events' },
  { id: 'document_upload', label: 'Document Upload', icon: FiUpload, category: 'Dealroom Events' },
  { id: 'document_approve', label: 'Document Approved', icon: FiCheck, category: 'Dealroom Events' },
  { id: 'referral_complete', label: 'Referral Completed', icon: FiUserPlus, category: 'Referral Events' },
  { id: 'challenge_complete', label: 'Challenge Completed', icon: FiTarget, category: 'Challenge Events' },
  { id: 'payment_due', label: 'Payment Due', icon: FiDollarSign, category: 'Billing Events' },
  { id: 'message_received', label: 'Message Received', icon: FiMessageSquare, category: 'Communication Events' },
  { id: 'schedule_daily', label: 'Schedule (Daily)', icon: FiClock, category: 'Time-based' },
  { id: 'schedule_weekly', label: 'Schedule (Weekly)', icon: FiCalendar, category: 'Time-based' },
  { id: 'schedule_monthly', label: 'Schedule (Monthly)', icon: FiCalendar, category: 'Time-based' },
  { id: 'api_webhook', label: 'API Webhook', icon: FiCode, category: 'External' }
];

const actionTypes = [
  { id: 'send_email', label: 'Send Email', icon: FiMail, category: 'Communication' },
  { id: 'send_notification', label: 'Send Notification', icon: FiBell, category: 'Communication' },
  { id: 'send_sms', label: 'Send SMS', icon: FiSend, category: 'Communication' },
  { id: 'award_points', label: 'Award Points', icon: FiGift, category: 'Rewards' },
  { id: 'create_task', label: 'Create Task', icon: FiCheck, category: 'Productivity' },
  { id: 'update_status', label: 'Update Status', icon: FiRefreshCw, category: 'System' },
  { id: 'generate_report', label: 'Generate Report', icon: FiFileText, category: 'Analytics' },
  { id: 'ai_analyze', label: 'AI Analysis', icon: FiCpu, category: 'AI' },
  { id: 'ai_recommend', label: 'AI Recommendations', icon: FiTarget, category: 'AI' },
  { id: 'webhook_call', label: 'Call Webhook', icon: FiCode, category: 'Integration' },
  { id: 'database_update', label: 'Update Database', icon: FiDatabase, category: 'System' },
  { id: 'assign_user', label: 'Assign to User', icon: FiUser, category: 'Productivity' }
];

const categories = ['All', 'Communication', 'Rewards', 'Marketing', 'Dealrooms', 'AI', 'Billing', 'Analytics', 'Challenges'];

const AutomationCenter = () => {
  const [automations, setAutomations] = useState(mockAutomations);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [showAIOnly, setShowAIOnly] = useState(false);
  
  const [newAutomation, setNewAutomation] = useState({
    name: '',
    description: '',
    category: 'Communication',
    trigger: 'user_registration',
    actions: [],
    conditions: [],
    scope: 'Platform-wide',
    aiPowered: false
  });

  const toggleAutomation = (id) => {
    setAutomations(automations.map(auto =>
      auto.id === id
        ? { ...auto, status: auto.status === 'active' ? 'paused' : 'active' }
        : auto
    ));
  };

  const deleteAutomation = (id) => {
    if (confirm('Are you sure you want to delete this automation?')) {
      setAutomations(automations.filter(auto => auto.id !== id));
    }
  };

  const addAction = (actionId) => {
    if (!newAutomation.actions.includes(actionId)) {
      setNewAutomation({
        ...newAutomation,
        actions: [...newAutomation.actions, actionId]
      });
    }
  };

  const removeAction = (actionId) => {
    setNewAutomation({
      ...newAutomation,
      actions: newAutomation.actions.filter(a => a !== actionId)
    });
  };

  const createAutomation = () => {
    if (!newAutomation.name || newAutomation.actions.length === 0) {
      alert('Please provide a name and at least one action');
      return;
    }

    const automation = {
      id: automations.length + 1,
      name: newAutomation.name,
      description: newAutomation.description,
      category: newAutomation.category,
      trigger: triggerTypes.find(t => t.id === newAutomation.trigger)?.label || 'User Registration',
      actions: newAutomation.actions.map(a => actionTypes.find(at => at.id === a)?.label).filter(Boolean),
      status: 'active',
      executions: 0,
      lastRun: 'Never',
      created: new Date().toISOString().split('T')[0],
      scope: newAutomation.scope,
      aiPowered: newAutomation.aiPowered
    };

    setAutomations([...automations, automation]);
    setIsCreating(false);
    setNewAutomation({
      name: '',
      description: '',
      category: 'Communication',
      trigger: 'user_registration',
      actions: [],
      conditions: [],
      scope: 'Platform-wide',
      aiPowered: false
    });
  };

  const filteredAutomations = automations.filter(auto => {
    const matchesCategory = selectedCategory === 'All' || auto.category === selectedCategory;
    const matchesSearch = auto.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         auto.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesAI = !showAIOnly || auto.aiPowered;
    return matchesCategory && matchesSearch && matchesAI;
  });

  const stats = {
    total: automations.length,
    active: automations.filter(a => a.status === 'active').length,
    totalExecutions: automations.reduce((sum, a) => sum + a.executions, 0),
    aiPowered: automations.filter(a => a.aiPowered).length
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Communication': FiMail,
      'Rewards': FiGift,
      'Marketing': FiBell,
      'Dealrooms': FiBriefcase,
      'AI': FiCpu,
      'Billing': FiDollarSign,
      'Analytics': FiTrendingUp,
      'Challenges': FiTarget
    };
    return icons[category] || FiZap;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Automation Center</h2>
          <p className="text-sm text-gray-500 mt-1">Create and manage platform-wide automation rules</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg transition-all"
        >
          <SafeIcon icon={FiPlus} className="w-5 h-5" />
          Create Automation
        </motion.button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-sm border border-blue-200 p-5"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500 rounded-xl">
              <SafeIcon icon={FiZap} className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-blue-700 font-medium">Total Automations</p>
              <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm border border-green-200 p-5"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-500 rounded-xl">
              <SafeIcon icon={FiPlay} className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-green-700 font-medium">Active</p>
              <p className="text-2xl font-bold text-green-900">{stats.active}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-sm border border-purple-200 p-5"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-500 rounded-xl">
              <SafeIcon icon={FiActivity} className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-purple-700 font-medium">Total Executions</p>
              <p className="text-2xl font-bold text-purple-900">{stats.totalExecutions.toLocaleString()}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl shadow-sm border border-indigo-200 p-5"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-500 rounded-xl">
              <SafeIcon icon={FiCpu} className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-indigo-700 font-medium">AI-Powered</p>
              <p className="text-2xl font-bold text-indigo-900">{stats.aiPowered}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search automations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          <button
            onClick={() => setShowAIOnly(!showAIOnly)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              showAIOnly
                ? 'bg-indigo-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <SafeIcon icon={FiCpu} className="w-4 h-4" />
            AI Only
          </button>
        </div>
      </div>

      {/* Automations List */}
      <div className="space-y-3">
        {filteredAutomations.map((automation, index) => {
          const CategoryIcon = getCategoryIcon(automation.category);
          
          return (
            <motion.div
              key={automation.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <div className="p-2 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg">
                        <SafeIcon icon={CategoryIcon} className="w-5 h-5 text-blue-600" />
                      </div>
                      <h4 className="text-lg font-bold text-gray-900">{automation.name}</h4>
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        automation.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : automation.status === 'beta'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {automation.status === 'active' ? 'Active' : automation.status === 'beta' ? 'Beta' : 'Paused'}
                      </span>
                      {automation.aiPowered && (
                        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 flex items-center gap-1">
                          <SafeIcon icon={FiCpu} className="w-3 h-3" />
                          AI-Powered
                        </span>
                      )}
                      <span className="px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                        {automation.category}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{automation.description}</p>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm mb-3">
                      <div className="flex items-center gap-2 text-gray-600">
                        <SafeIcon icon={FiZap} className="w-4 h-4 text-blue-500" />
                        <span className="font-medium">Trigger:</span>
                        <span>{automation.trigger}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <SafeIcon icon={FiLayers} className="w-4 h-4 text-purple-500" />
                        <span className="font-medium">Scope:</span>
                        <span>{automation.scope}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <SafeIcon icon={FiActivity} className="w-4 h-4 text-green-500" />
                        <span>{automation.executions.toLocaleString()} executions</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <SafeIcon icon={FiClock} className="w-4 h-4 text-gray-500" />
                        <span>Last run: {automation.lastRun}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {automation.actions.map((action, idx) => (
                        <span key={idx} className="px-3 py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 text-xs font-medium rounded-lg border border-blue-200">
                          {action}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleAutomation(automation.id)}
                      className={`p-2 rounded-lg transition-colors ${
                        automation.status === 'active'
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-gray-400 hover:bg-gray-50'
                      }`}
                      title={automation.status === 'active' ? 'Pause' : 'Activate'}
                    >
                      <SafeIcon icon={automation.status === 'active' ? FiToggleRight : FiToggleLeft} className="w-6 h-6" />
                    </button>
                    <button
                      onClick={() => setSelectedAutomation(automation)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <SafeIcon icon={FiEdit3} className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => deleteAutomation(automation.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {filteredAutomations.length === 0 && (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <SafeIcon icon={FiZap} className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No automations found</h3>
          <p className="text-gray-600 mb-6">Try adjusting your filters or create a new automation</p>
          <button
            onClick={() => setIsCreating(true)}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-5 rounded-lg"
          >
            <SafeIcon icon={FiPlus} />
            Create Automation
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {isCreating && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setIsCreating(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-2xl">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">Create Automation</h3>
                    <p className="text-sm text-gray-500 mt-1">Set up a new platform-wide automation rule</p>
                  </div>
                  <button
                    onClick={() => setIsCreating(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <SafeIcon icon={FiX} className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Automation Name *
                    </label>
                    <input
                      type="text"
                      value={newAutomation.name}
                      onChange={(e) => setNewAutomation({ ...newAutomation, name: e.target.value })}
                      placeholder="e.g., Welcome Email Sequence"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={newAutomation.description}
                      onChange={(e) => setNewAutomation({ ...newAutomation, description: e.target.value })}
                      placeholder="Describe what this automation does..."
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Category *
                      </label>
                      <select
                        value={newAutomation.category}
                        onChange={(e) => setNewAutomation({ ...newAutomation, category: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {categories.filter(c => c !== 'All').map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Scope
                      </label>
                      <select
                        value={newAutomation.scope}
                        onChange={(e) => setNewAutomation({ ...newAutomation, scope: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option>Platform-wide</option>
                        <option>Dealrooms</option>
                        <option>Referral System</option>
                        <option>Billing System</option>
                        <option>Team Management</option>
                        <option>Challenges</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                    <input
                      type="checkbox"
                      id="ai-powered"
                      checked={newAutomation.aiPowered}
                      onChange={(e) => setNewAutomation({ ...newAutomation, aiPowered: e.target.checked })}
                      className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                    <label htmlFor="ai-powered" className="flex items-center gap-2 text-sm font-medium text-indigo-900 cursor-pointer">
                      <SafeIcon icon={FiCpu} className="w-5 h-5" />
                      Enable AI-Powered Features
                    </label>
                  </div>
                </div>

                {/* Trigger */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    When this happens (Trigger) *
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-96 overflow-y-auto p-1">
                    {Object.entries(
                      triggerTypes.reduce((acc, trigger) => {
                        if (!acc[trigger.category]) acc[trigger.category] = [];
                        acc[trigger.category].push(trigger);
                        return acc;
                      }, {})
                    ).map(([category, triggers]) => (
                      <div key={category} className="col-span-2">
                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{category}</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {triggers.map(trigger => (
                            <button
                              key={trigger.id}
                              onClick={() => setNewAutomation({ ...newAutomation, trigger: trigger.id })}
                              className={`flex items-center gap-3 p-3 border-2 rounded-lg transition-all ${
                                newAutomation.trigger === trigger.id
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <SafeIcon icon={trigger.icon} className={`w-4 h-4 ${
                                newAutomation.trigger === trigger.id ? 'text-blue-600' : 'text-gray-600'
                              }`} />
                              <span className={`font-medium text-sm ${
                                newAutomation.trigger === trigger.id ? 'text-blue-900' : 'text-gray-700'
                              }`}>
                                {trigger.label}
                              </span>
                              {newAutomation.trigger === trigger.id && (
                                <SafeIcon icon={FiCheck} className="ml-auto w-5 h-5 text-blue-600" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Do these actions * (Select multiple)
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-96 overflow-y-auto p-1">
                    {Object.entries(
                      actionTypes.reduce((acc, action) => {
                        if (!acc[action.category]) acc[action.category] = [];
                        acc[action.category].push(action);
                        return acc;
                      }, {})
                    ).map(([category, actions]) => (
                      <div key={category} className="col-span-2">
                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{category}</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {actions.map(action => (
                            <button
                              key={action.id}
                              onClick={() => {
                                if (newAutomation.actions.includes(action.id)) {
                                  removeAction(action.id);
                                } else {
                                  addAction(action.id);
                                }
                              }}
                              className={`flex items-center gap-3 p-3 border-2 rounded-lg transition-all ${
                                newAutomation.actions.includes(action.id)
                                  ? 'border-green-500 bg-green-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <SafeIcon icon={action.icon} className={`w-4 h-4 ${
                                newAutomation.actions.includes(action.id) ? 'text-green-600' : 'text-gray-600'
                              }`} />
                              <span className={`font-medium text-sm flex-1 text-left ${
                                newAutomation.actions.includes(action.id) ? 'text-green-900' : 'text-gray-700'
                              }`}>
                                {action.label}
                              </span>
                              {newAutomation.actions.includes(action.id) && (
                                <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Info Banners */}
                <div className="space-y-3">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                    <SafeIcon icon={FiShield} className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-blue-900">HIPAA Compliant</p>
                      <p className="text-xs text-blue-700 mt-1">
                        All automations are logged and comply with HIPAA regulations. Sensitive data is encrypted.
                      </p>
                    </div>
                  </div>

                  {newAutomation.aiPowered && (
                    <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg flex items-start gap-3">
                      <SafeIcon icon={FiCpu} className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-indigo-900">AI-Enhanced Automation</p>
                        <p className="text-xs text-indigo-700 mt-1">
                          This automation will use artificial intelligence to analyze data, make predictions, and optimize outcomes.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 rounded-b-2xl">
                <button
                  onClick={() => setIsCreating(false)}
                  className="py-3 px-6 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={createAutomation}
                  disabled={!newAutomation.name || newAutomation.actions.length === 0}
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold py-3 px-6 rounded-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed hover:from-blue-700 hover:to-indigo-700 transition-all"
                >
                  <SafeIcon icon={FiZap} />
                  Create Automation
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AutomationCenter;